// src/pages/NotFoundPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Home } from "lucide-react";
import { useKuizyTheme } from "@/styles/kuizyTheme";
import ThemeMenu from "@/widgets/ThemeMenu";

const fade = { initial:{opacity:0,y:16}, animate:{opacity:1,y:0}, transition:{duration:.28} };

export default function NotFoundPage() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { t } = useKuizyTheme();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = "ไม่พบหน้านี้ • Kuizy";
    setLoaded(true);
  }, []);

  const landing = useMemo(() => {
    try {
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
      if (Array.isArray(roles) && roles.includes("LEARNER")) return "/learn/login";
    } catch {}
    return "/login";
  }, []);

  return (
    <div className={`relative min-h-screen ${t.bg}`}>
      <ThemeMenu />

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-28 h-80 w-80 rounded-full blur-3xl opacity-30 bg-primary" />
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-30 bg-secondary" />
      </div>

      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div {...fade} className="w-full max-w-3xl">
          <div className="mx-auto w-full">
            <div className="relative rounded-3xl p-[2px] bg-gradient-to-br from-primary/70 to-secondary/70 shadow-2xl">
              <div className={`rounded-3xl p-8 md:p-12 ${t.card}`}>
                <div className="flex items-center justify-center gap-3">
                  <Sparkles className={`size-6 opacity-80 ${t.text}`} />
                  <div className={`tracking-wide text-sm md:text-base ${t.text}`}>Kuizy</div>
                </div>

                <div className="mt-4 text-center">
                  <div className="inline-block">
                    <div className="text-7xl md:text-8xl font-black leading-none">
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-primary">
                        404
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className={`text-2xl md:text-3xl font-bold tracking-wide ${t.text}`}>ไม่พบหน้านี้</div>
                    <div className={`mt-2 text-sm md:text-base leading-relaxed ${t.subtext}`}>
                      ลิงก์ที่เข้าถึง
                      <span className={`ml-2 px-2 py-0.5 rounded-md align-baseline ${t.soft}`}>{pathname}</span>
                      <span className="ml-1">อาจถูกย้ายหรือลบออก</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  <button
                    onClick={()=>nav(-1)}
                    className={`h-11 rounded-xl ${t.neutralBtn} hover:opacity-90 transition`}
                  >
                    กลับหน้าที่แล้ว
                  </button>
                  <button
                    onClick={()=>nav(landing, { replace: true })}
                    className="h-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white flex items-center justify-center gap-2 hover:opacity-90 transition"
                    disabled={!loaded}
                  >
                    <Home className="size-4" /> ไปหน้าเริ่มต้น
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
